import { apiClient } from '../../../services/api/client';

// Gate token proving a real password login (or trusted infoscreen device) happened
// in this browser tab. Lives in sessionStorage on purpose: it must not survive past
// the current tab/browser session, so PIN login on the Switch User page can never be
// reached by a caller that never had a real login here.
export const SWITCH_SESSION_KEY = 'orbit_switch_session';

export class AuthService {
  async login(credentials) {
    try {
      const response = await apiClient.post('/api/v1/ac/auth/login', credentials);
      const { token, refresh_token, switch_session_token } = response.data;

      // Store tokens
      localStorage.setItem('access_token', token);
      localStorage.setItem('refresh_token', refresh_token);

      if (switch_session_token) {
        sessionStorage.setItem(SWITCH_SESSION_KEY, switch_session_token);
      }

      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async loginWithPin(credentials) {
    try {
      const switchSessionToken = sessionStorage.getItem(SWITCH_SESSION_KEY);
      const response = await apiClient.post('/api/v1/ac/auth/login-with-pin', credentials, {
        headers: switchSessionToken ? { 'X-Switch-Session-Token': switchSessionToken } : {},
      });
      const { token, refresh_token, switch_session_token } = response.data;

      localStorage.setItem('access_token', token);
      localStorage.setItem('refresh_token', refresh_token);

      // Server rotates the gate token on every PIN login — store the fresh one.
      if (switch_session_token) {
        sessionStorage.setItem(SWITCH_SESSION_KEY, switch_session_token);
      }

      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  hasSwitchSession() {
    return !!sessionStorage.getItem(SWITCH_SESSION_KEY);
  }

  // Refreshes the switch-session gate token for an already-authenticated
  // session. Called alongside the staff-list fetch that populates orbit_users
  // for the Switch User page, so PIN login stays available there without
  // requiring a fresh password login each time.
  async refreshSwitchSession() {
    try {
      const response = await apiClient.post('/api/v1/ac/auth/switch-session/refresh');
      const { switch_session_token } = response.data;
      if (switch_session_token) {
        sessionStorage.setItem(SWITCH_SESSION_KEY, switch_session_token);
      }
      return switch_session_token;
    } catch (error) {
      console.warn('Failed to refresh switch-session token:', error);
      return null;
    }
  }

  clearSwitchSession() {
    sessionStorage.removeItem(SWITCH_SESSION_KEY);
  }

  async logout() {
    try {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (accessToken && refreshToken) {
        await apiClient.post('/api/v1/ac/auth/logout', {
          token: accessToken,
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local tokens
      this.clearTokens();
    }
  }

  async verifyToken(token) {
    try {
      const response = await apiClient.post('/api/v1/ac/auth/verify', { token });
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  async refreshToken() {
    try {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post('/api/v1/ac/auth/refresh', {
        token: accessToken,
        refresh_token: refreshToken,
      });

      const { token: newAccessToken, refresh_token: newRefreshToken } = response.data;

      localStorage.setItem('access_token', newAccessToken);
      localStorage.setItem('refresh_token', newRefreshToken);

      return response.data;
    } catch (error) {
      this.clearTokens();
      throw this.handleAuthError(error);
    }
  }

  async getCurrentUser() {
    try {
      const response = await apiClient.get('/api/v1/ac/staff-members/me');
      return response.data;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }

  getToken() {
    return localStorage.getItem('access_token');
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  handleAuthError(error) {
    const errorData = error.response?.data;

    if (errorData?.error_code) {
      const fallbackMessages = {
        STAFF_MEMBER__INVALID_CREDS: 'Invalid username or password',
        STAFF_MEMBER__INACTIVE: 'Your account is currently inactive',
        DEPARTMENT__NOT_FOUND: 'Department not found',
        LOGIN_ATTEMPTS__TOO_MANY: 'Too many failed login attempts. Please try again later',
        TOKEN__EXPIRED: 'Session expired. Please log in again',
        TOKEN__INVALID: 'Invalid authentication token',
        SWITCH_SESSION__REQUIRED: 'PIN login is not available. Please use your password.',
        PIN__NOT_AVAILABLE: 'PIN login is not available for this account. Please use your password.',
        PIN__LOCKED: 'Too many failed PIN attempts. Please log in with your password.',
      };

      // Prefer the backend's message (e.g. PIN attempts-remaining count) over the
      // generic fallback, falling back only when the backend didn't send one.
      const message = errorData.message || fallbackMessages[errorData.error_code] || 'Authentication failed';
      const authError = new Error(message);
      authError.code = errorData.error_code;
      return authError;
    }

    return new Error(error.message || 'Network error occurred');
  }
}

export const authService = new AuthService();